import { useEffect, useState } from "react";
import { Button } from "src/components/ui/Button";
import { Label } from "src/components/ui/Label";
import { Textarea } from "src/components/ui/TextArea";
import { useToast } from "src/hooks/useToast";
import { usePageHeaderStore } from "src/store/pageHeaderStore";
import { nodeFeedbackService, type NodeFeedbackLevel, type NodeFeedbackRequest, type NodeFeedbackSession } from "src/services/nodeFeedbackService";

const levels: Array<{ value: NodeFeedbackLevel; label: string }> = [
  { value: "developing", label: "Developing" },
  { value: "on_track", label: "On track" },
  { value: "secured", label: "Secured" },
];

const TutorNodeFeedback = () => {
  const { toast } = useToast();
  const { setTitle, setDescription, reset } = usePageHeaderStore();
  const [requests, setRequests] = useState<NodeFeedbackRequest[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [sessions, setSessions] = useState<NodeFeedbackSession[]>([]);
  const [level, setLevel] = useState<NodeFeedbackLevel>("developing");
  const [feedback, setFeedback] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const selected = requests.find((request) => request.id === selectedId) || null;

  useEffect(() => {
    setTitle("Node feedback");
    setDescription("Feedback for a finished lesson, unit, or domain");
    return () => reset();
  }, [reset, setDescription, setTitle]);

  useEffect(() => {
    let current = true;
    nodeFeedbackService.listForTutor().then((rows) => {
      if (!current) return;
      setRequests(rows);
      setSelectedId(rows[0]?.id || "");
    }).catch((error) => {
      if (current) toast({ title: "Unable to load feedback requests", description: error instanceof Error ? error.message : "Please try again.", type: "error" });
    }).finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [toast]);

  useEffect(() => {
    if (!selected) return;
    setLevel(selected.progressLevel || "developing");
    setFeedback(selected.detailedFeedback);
    let current = true;
    nodeFeedbackService.listSessions(selected.id).then((rows) => { if (current) setSessions(rows); }).catch((error) => {
      if (current) toast({ title: "Unable to load session feedback", description: error instanceof Error ? error.message : "Please try again.", type: "error" });
    });
    return () => { current = false; };
  }, [selectedId]);

  const submit = async () => {
    if (!selected || saving) return;
    setSaving(true);
    try {
      await nodeFeedbackService.submit(selected.id, level, feedback);
      setRequests((rows) => rows.map((row) => row.id === selected.id ? { ...row, status: "submitted", progressLevel: level, detailedFeedback: feedback.trim() } : row));
      toast({ title: "Feedback saved", description: "This records progress and does not mark the node proficient.", type: "success" });
    } catch (error) {
      toast({ title: "Unable to save feedback", description: error instanceof Error ? error.message : "Please try again.", type: "error" });
    } finally { setSaving(false); }
  };

  return <div className="grid gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
    <aside className="rounded-xl border border-slate-200 bg-white p-3">
      <h2 className="px-2 pb-2 text-sm font-semibold text-slate-700">Requests</h2>
      {loading && <p className="px-2 text-sm text-slate-500">Loading…</p>}
      {!loading && requests.length === 0 && <p className="px-2 text-sm text-slate-500">No lesson, unit, or domain is waiting for your feedback.</p>}
      <div className="space-y-1">{requests.map((request) => <button key={request.id} onClick={() => setSelectedId(request.id)} className={`w-full rounded-md px-2 py-2 text-left text-sm ${request.id === selectedId ? "bg-violet-100 text-violet-950" : "hover:bg-slate-50"}`}><span className="block font-medium">{request.nodeName}</span><span className="text-xs capitalize text-slate-500">{request.nodeType} · {request.status === "open" ? "Not recorded yet" : request.progressLevel?.replace("_", " ")}</span></button>)}</div>
    </aside>
    <section className="rounded-xl border border-slate-200 bg-white p-5">
      {!selected && <p className="text-sm text-slate-500">Select a request to review the sessions that covered it.</p>}
      {selected && <div className="space-y-5">
        <div><p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{selected.nodeType}</p><h2 className="text-xl font-semibold text-slate-900">{selected.nodeName}</h2>{selected.prompt && <p className="mt-2 text-sm text-slate-600">{selected.prompt}</p>}</div>
        <div className="space-y-3">
          <h3 className="font-semibold">Session feedback</h3>
          {sessions.length === 0 && <p className="text-sm text-slate-500">No ended sessions are linked to this node yet.</p>}
          {sessions.map((session) => <article key={session.id} className="rounded-lg border border-slate-200 p-3"><p className="text-sm font-medium">{session.objective_statement}</p><p className="text-xs text-slate-500">{new Date(session.scheduled_at).toLocaleString()} · {session.tutor_name || "Tutor"}</p>{session.feedback.length === 0 && <p className="mt-2 text-sm text-slate-500">No session feedback was recorded.</p>}{session.feedback.map((item) => <div key={item.id} className="mt-2 text-sm text-slate-700"><p className="capitalize">{item.user_role}{item.rating ? ` · ${item.rating}/5` : ""}</p>{item.comments && <p className="mt-1">{item.comments}</p>}</div>)}</article>)}
        </div>
        <fieldset className="space-y-2"><legend className="text-sm font-medium">Progress level</legend><div className="flex flex-wrap gap-2">{levels.map((item) => <label key={item.value} className={`rounded-full border px-3 py-1.5 text-sm ${level === item.value ? "border-violet-600 bg-violet-50 text-violet-900" : "border-slate-200"}`}><input className="sr-only" type="radio" name="progress-level" value={item.value} checked={level === item.value} onChange={() => setLevel(item.value)} />{item.label}</label>)}</div></fieldset>
        <div><Label htmlFor="node-feedback">Detailed feedback</Label><Textarea id="node-feedback" rows={6} value={feedback} onChange={(event) => setFeedback(event.target.value)} /></div>
        <Button disabled={saving} onClick={() => void submit()}>{saving ? "Saving…" : selected.status === "submitted" ? "Update feedback" : "Submit feedback"}</Button>
        <p className="text-xs text-slate-500">You can submit this any time. It does not block the student and does not mark the node proficient.</p>
      </div>}
    </section>
  </div>;
};

export default TutorNodeFeedback;
